import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { messages } from './messages';

import type { SideNavModalItem } from './index';
import { SideNavModal } from './index';

export type SideNavModalOpenOptions = {
	title?: React.ReactNode;
	searchPlaceholder?: string;
	navAriaLabel?: string;
	/** Id of the side nav item to select when the modal opens (e.g. 'connected-apps') */
	defaultSelectedId?: string;
	/** When true, Connected apps content scrolls to the "available to connect" section after open */
	scrollToAvailableApps?: boolean;
	/** Identifies what surface opened the modal (e.g. 'chat', 'search-qf-connect-apps-pill') for analytics attribution */
	surface?: string;
};

type SideNavModalScrollContextValue = {
	scrollToAvailableApps: boolean;
};

const SideNavModalScrollContext = React.createContext<SideNavModalScrollContextValue>({
	scrollToAvailableApps: false,
});

type SideNavModalContextValue = {
	open: (options: SideNavModalOpenOptions) => void;
	close: () => void;
	isOpen: boolean;
	disabled: boolean;
};

const SideNavModalContext = React.createContext<SideNavModalContextValue | null>(null);

const SIDE_NAV_MODAL_STUB: SideNavModalContextValue = {
	open: () => {},
	close: () => {},
	isOpen: false,
	disabled: true,
};

export type SideNavModalProviderProps = {
	children: React.ReactNode;
	/** Side nav items to display in the modal. Defined once at app level. */
	items: SideNavModalItem[];
	/**
	 * When true, the modal is disabled and `useSideNavModal()` returns a no-op stub.
	 * The provider stays mounted but all open/close triggers become inert.
	 * Use this for constrained viewports (e.g. Chrome extension sidebar) where
	 * the large modal cannot render properly.
	 * @default false
	 */
	disabled?: boolean;
	testId?: string;
};

type SideNavModalRendererProps = {
	isOpen: boolean;
	onClose: () => void;
	modalOptions: SideNavModalOpenOptions | null;
	items: SideNavModalItem[];
	testId?: string;
};

const SideNavModalRenderer = ({
	isOpen,
	onClose,
	modalOptions,
	items,
	testId,
}: SideNavModalRendererProps) => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const handleActiveTabChange = useCallback(
		(tabId: string) => {
			sendAnalyticsEvent({
				action: 'viewed',
				actionSubject: 'tab',
				actionSubjectId: 'rovoSettingsModalTab',
				attributes: {
					tabId,
					...(modalOptions?.surface !== undefined && {
						surface: modalOptions.surface,
					}),
				},
			});
		},
		[sendAnalyticsEvent, modalOptions?.surface],
	);

	return (
		<SideNavModal
			isOpen={isOpen}
			onClose={onClose}
			title={modalOptions?.title ?? formatMessage(messages.title)}
			items={items}
			defaultSelectedId={modalOptions?.defaultSelectedId}
			searchPlaceholder={modalOptions?.searchPlaceholder}
			navAriaLabel={modalOptions?.navAriaLabel}
			testId={testId}
			onActiveTabChange={handleActiveTabChange}
		/>
	);
};

export const SideNavModalProvider = ({
	children,
	items,
	disabled = false,
	testId,
}: SideNavModalProviderProps): React.JSX.Element => {
	const { sendAnalyticsEvent } = useAnalytics();
	const [isOpen, setIsOpen] = useState(false);
	const [modalOptions, setModalOptions] = useState<SideNavModalOpenOptions | null>(null);
	const open = useCallback(
		(options: SideNavModalOpenOptions) => {
			setModalOptions(options);
			setIsOpen(true);

			const initialTabId = options.defaultSelectedId ?? items[0]?.id;

			sendAnalyticsEvent({
				action: 'viewed',
				actionSubject: 'modal',
				actionSubjectId: 'rovoSettingsModal',
				attributes: {
					...(options.defaultSelectedId !== undefined && {
						defaultSelectedId: options.defaultSelectedId,
					}),
					...(options.scrollToAvailableApps !== undefined && {
						scrollToAvailableApps: options.scrollToAvailableApps,
					}),
					...(options.surface !== undefined && {
						surface: options.surface,
					}),
				},
			});

			if (initialTabId) {
				sendAnalyticsEvent({
					action: 'viewed',
					actionSubject: 'tab',
					actionSubjectId: 'rovoSettingsModalTab',
					attributes: {
						tabId: initialTabId,
						...(options.surface !== undefined && {
							surface: options.surface,
						}),
					},
				});
			}
		},
		[sendAnalyticsEvent, items],
	);

	const close = useCallback(() => {
		setIsOpen(false);
		setModalOptions(null);
	}, []);

	// Clean up open state when disabled transitions to true at runtime
	useEffect(() => {
		if (disabled) {
			setIsOpen(false);
			setModalOptions(null);
		}
	}, [disabled]);

	const value = useMemo<SideNavModalContextValue>(
		() => (disabled ? SIDE_NAV_MODAL_STUB : { open, close, isOpen, disabled: false }),
		[disabled, open, close, isOpen],
	);

	const scrollContextValue = useMemo<SideNavModalScrollContextValue>(
		() => ({
			scrollToAvailableApps: Boolean(!disabled && isOpen && modalOptions?.scrollToAvailableApps),
		}),
		[disabled, isOpen, modalOptions?.scrollToAvailableApps],
	);

	return (
		<SideNavModalContext.Provider value={value}>
			<SideNavModalScrollContext.Provider value={scrollContextValue}>
				{children}
				{!disabled && isOpen && (
					<SideNavModalRenderer
						isOpen={isOpen}
						onClose={close}
						modalOptions={modalOptions}
						items={items}
						testId={testId}
					/>
				)}
			</SideNavModalScrollContext.Provider>
		</SideNavModalContext.Provider>
	);
};

/** True while the Rovo settings modal is open and was requested to scroll to available apps */
export const useSideNavModalScrollToAvailableApps = (): boolean =>
	useContext(SideNavModalScrollContext).scrollToAvailableApps;

export const useSideNavModal = (): SideNavModalContextValue => {
	const context = useContext(SideNavModalContext);

	/**
	 * Return the real modal API only when `SideNavModalProvider` is mounted.
	 * Otherwise return a no-op stub for tests or trees that have not wrapped
	 * with the provider yet.
	 */
	if (context !== null) {
		return context;
	}

	return SIDE_NAV_MODAL_STUB;
};
